/**
 * Every date this app shows a person, in one format and one locale.
 *
 * <p><b>Australian, always.</b> Not the browser's. A call with no locale renders in whatever the
 * machine is set to, so the same consignment read 15/09/2026 on one desk and 09/15/2026 on the next
 * - and a numeric date is the one thing nobody can tell they are misreading. The pickers were
 * already pinned to en-AU in main.tsx; everything that *displays* a date was not.</p>
 *
 * <p><b>Numeric, always.</b> 15/09/2026 rather than 15 Sept 2026, chosen so a date is the same width
 * and the same shape wherever it appears. The month name is easier to read in isolation and harder
 * to scan down a column, and having two house styles is how a third one starts.</p>
 *
 * <p>Everything here takes whatever the API gave us - a string, a Date, null - and returns a dash
 * for anything it cannot read. A row that renders "Invalid Date" is worse than one that admits it
 * has no date. Pass a <code>fallback</code> of <code>''</code> where the date sits inside a
 * sentence and a dash would read as a stray hyphen rather than as an absence.</p>
 */

/** What an absent or unreadable date reads as. */
export const NO_DATE = '-';

const DATE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const TIME: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/** Parsed, or null when there is nothing readable there. */
function parse(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

/** 15/09/2026 */
export function formatDate(
  value: string | number | Date | null | undefined,
  fallback = NO_DATE,
): string {
  const date = parse(value);
  return date ? date.toLocaleDateString('en-AU', DATE) : fallback;
}

/** 15/09/2026, 2:30 pm */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback = NO_DATE,
): string {
  const date = parse(value);
  return date ? date.toLocaleString('en-AU', { ...DATE, ...TIME }) : fallback;
}

/** 2:30 pm */
export function formatTime(
  value: string | number | Date | null | undefined,
  fallback = NO_DATE,
): string {
  const date = parse(value);
  return date ? date.toLocaleTimeString('en-AU', TIME) : fallback;
}

/**
 * Mon 15/09/2026
 *
 * <p>For the screens that plan a day rather than record one - a delivery window, a run date - where
 * which day of the week it falls on is the thing being decided and reading it off a numeric date is
 * a mental step nobody should have to take.</p>
 */
export function formatDayDate(
  value: string | number | Date | null | undefined,
  fallback = NO_DATE,
): string {
  const date = parse(value);
  if (!date) return fallback;

  return `${date.toLocaleDateString('en-AU', { weekday: 'short' })} ${formatDate(date)}`;
}

/** Mon 15/09/2026, 2:30 pm */
export function formatDayDateTime(
  value: string | number | Date | null | undefined,
  fallback = NO_DATE,
): string {
  const date = parse(value);
  if (!date) return fallback;

  return `${date.toLocaleDateString('en-AU', { weekday: 'short' })} ${formatDateTime(date)}`;
}

/**
 * Whether there is a date here at all, for a caller deciding between a date and a dash of its own.
 */
export function hasDate(value: string | number | Date | null | undefined): boolean {
  return parse(value) !== null;
}
